import { useQuery } from '@tanstack/react-query';
import { isNil } from 'lodash';
import { ApiService } from 'api';

export const ALEXANDRIA_COMPANY_SEARCH_QUERY_KEY = 'ALEXANDRIA_COMPANY_SEARCH';
export const ALEXANDRIA_COMPANY_PROFILE_QUERY_KEY = 'ALEXANDRIA_COMPANY_PROFILE';

interface AlexandriaSearchParams {
  firmId?: number | null;
  companyId?: number | null;
  // Defaults to the company's own name/website on the backend.
  name?: string;
  enabled?: boolean;
}

// Upstream call, not a local lookup — don't refetch on every remount.
const FIVE_MINUTES_MS = 5 * 60 * 1000;

export const useAlexandriaCompanySearch = ({ firmId, companyId, name, enabled = true }: AlexandriaSearchParams) =>
  useQuery({
    queryKey: [ALEXANDRIA_COMPANY_SEARCH_QUERY_KEY, firmId, companyId, name ?? null],
    queryFn: () => ApiService.apiFirmsDailyNavCompaniesAlexandriaSearchList(String(firmId), String(companyId), name),
    enabled: enabled && !isNil(firmId) && !isNil(companyId),
    staleTime: FIVE_MINUTES_MS,
    // "Not tracked" is a normal answer and a 502 means upstream is down;
    // retrying either just stalls the UI.
    retry: false,
  });

interface AlexandriaProfileParams {
  firmId?: number | null;
  companyId?: number | null;
  /** Draft link to describe. The saved one is used when this is omitted. */
  alexandriaId?: string | null;
}

// Names an ID the caller already holds, so it can be asked for whenever a linked
// company is on screen — including by viewers, who see the label but cannot edit it.
export const useAlexandriaCompanyProfile = ({ firmId, companyId, alexandriaId }: AlexandriaProfileParams) =>
  useQuery({
    queryKey: [ALEXANDRIA_COMPANY_PROFILE_QUERY_KEY, firmId, companyId, alexandriaId ?? null],
    queryFn: () =>
      ApiService.apiFirmsDailyNavCompaniesAlexandriaProfileList(
        String(firmId),
        String(companyId),
        alexandriaId ?? undefined
      ),
    // Boolean, not isNil: a blank id is 'unlinked', and asking about it would
    // spend a request on a guaranteed 400.
    enabled: !isNil(firmId) && !isNil(companyId) && Boolean(alexandriaId),
    staleTime: FIVE_MINUTES_MS,
    // Same reasoning as the search: a 502 means upstream is down, and retrying
    // only delays the fallback to the raw ID.
    retry: false,
  });
